import { queryOptions } from '@tanstack/react-query';

import { $api } from '@shared/api';
import { routes } from '@shared/api/routes';

import type { Profile, ProfileDto } from '../model/types';

type GetProfileResponse = {
    status: 'success';
    data: ProfileDto;
};

type GenerateApiTokenResponse = {
    status: 'success';
    data: {
        token: string;
    };
};

type DeleteProfileResponse = {
    status: 'success';
    data: null;
};

type UpdateAvatarResponse = {
    status: 'success';
    data: {
        avatar_url: string;
    };
};

export type UpdateProfilePayload = {
    name?: string;
    field_activity_id?: number;
    city_id?: number;
    country_id?: number;
};

export type UpdateProfilePasswordPayload = {
    old_password: string;
    password: string;
    password_confirmation: string;
};

export type UpdateNotificationSubscriptionsPayload = {
    event_names: string[];
};

export type UpdateProfilePreferencesPayload = {
    need_show_tooltips: boolean;
};

export const profileQueryKeys = {
    all: ['profile'] as const,
    current: () => [...profileQueryKeys.all, 'current'] as const,
};

const mapProfile = (profile: ProfileDto): Profile => ({
    user: {
        id: profile.user.id,
        name: profile.user.name,
        email: profile.user.email,
        phone: profile.user.phone,
        avatarUrl: profile.user.avatar_url,
        fieldActivity: profile.user.field_activity,
        city: profile.user.city,
        country: profile.user.country
            ? {
                  id: profile.user.country.id,
                  name: profile.user.country.name,
                  code: profile.user.country.code,
                  alpha3Code: profile.user.country.alpha3_code,
                  flagPath: profile.user.country.flag_path,
                  flagUnicode: profile.user.country.flag_unicode,
              }
            : null,
    },
    tariff: {
        planId: profile.tariff.plan_id,
        title: profile.tariff.title,
        periodTitle: profile.tariff.period_title,
        isDefault: profile.tariff.is_default,
        isActive: profile.tariff.is_active,
        expiresAt: profile.tariff.expires_at,
        hasRecurrentPayment: profile.tariff.has_recurrent_payment,
    },
    preferences: {
        needShowTooltips: profile.preferences.need_show_tooltips,
    },
    notificationSubscriptions: profile.notification_subscriptions.map((subscription) => ({
        eventName: subscription.event_name,
        title: subscription.title,
        isSigned: subscription.is_signed,
    })),
    referral: {
        url: profile.referral.url,
        invitedCount: profile.referral.invited_count,
        paidCount: profile.referral.paid_count,
        paidSubscriptions: profile.referral.paid_subscriptions.map((subscription) => ({
            date: subscription.date,
            email: subscription.email,
            tariffPlanName: subscription.tariff_plan_name,
            price: subscription.price,
        })),
    },
    apiToken: {
        value: profile.api_token.value,
        canUsePublicApiByTariff: profile.api_token.can_use_public_api_by_tariff,
    },
    context: {
        locationType: profile.context.location_type,
        fieldActivities: profile.context.field_activities,
        cities: profile.context.cities,
        countries: profile.context.countries.map((country) => ({
            id: country.id,
            name: country.name,
            code: country.code,
            code3: country['code-3'],
            flagPath: country.flag_path,
            flagUnicode: country.flag_unicode,
            isDefault: country.is_default,
        })),
    },
});

export const profileApi = {
    getProfile: async ({ signal }: { signal?: AbortSignal } = {}) => {
        const response = await $api.get<GetProfileResponse>(routes.profile(), { signal });

        return mapProfile(response.data.data);
    },
    updateProfile: async (payload: UpdateProfilePayload) => {
        const response = await $api.patch<GetProfileResponse>(routes.profile(), payload);

        return mapProfile(response.data.data);
    },
    updatePassword: async (payload: UpdateProfilePasswordPayload) => {
        await $api.patch(routes.profilePassword(), payload);
    },
    updateNotificationSubscriptions: async (payload: UpdateNotificationSubscriptionsPayload) => {
        const response = await $api.patch<GetProfileResponse>(
            routes.profileNotificationSubscriptions(),
            payload
        );

        return mapProfile(response.data.data);
    },
    updatePreferences: async (payload: UpdateProfilePreferencesPayload) => {
        const response = await $api.patch<GetProfileResponse>(
            routes.profilePreferences(),
            payload
        );

        return mapProfile(response.data.data);
    },
    createApiToken: async () => {
        const response = await $api.post<GenerateApiTokenResponse>(routes.profileApiToken());

        return response.data.data.token;
    },
    refreshApiToken: async () => {
        const response = await $api.post<GenerateApiTokenResponse>(
            routes.profileApiTokenRefresh()
        );

        return response.data.data.token;
    },
    deleteProfile: async () => {
        await $api.delete<DeleteProfileResponse>(routes.profile());
    },
    updateAvatar: async (file: File) => {
        const formData = new FormData();

        formData.append('file', file);

        const response = await $api.post<UpdateAvatarResponse>(
            routes.profileAvatar(),
            formData
        );

        return response.data.data.avatar_url;
    },
    getProfileQueryOptions: () =>
        queryOptions({
            queryKey: profileQueryKeys.current(),
            queryFn: ({ signal }) => profileApi.getProfile({ signal }),
        }),
};
